import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { API_URL } from "../config/api";

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true || success === "true" || success === 1 || success === "1"
  );
}

function formatDateToIST(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  return date.toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function displayUser(ref) {
  if (!ref) return "-";
  if (typeof ref === "string") return ref;
  return ref.name || ref.email || "-";
}

function freightStatusBadge(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  let badge = "badge-secondary";
  if (normalized === "pending") badge = "badge-warning";
  if (normalized === "paid" || normalized === "completed") {
    badge = "badge-success";
  }
  if (normalized === "cancelled" || normalized === "canceled") {
    badge = "badge-danger";
  }
  return (
    <span
      className={`badge ${badge}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {capitalizeStatus(status)}
    </span>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="form-group row align-items-center">
      <label className="col-12 col-md-4 col-form-label">{label}</label>
      <div className="col-12 col-md-8">
        <div className="txtdsply">{children}</div>
      </div>
    </div>
  );
}

const FrightTrackerView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [freight, setFreight] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFreight = async () => {
      if (!id) {
        toast.error("Freight ID is missing", { autoClose: 2000 });
        navigate("/frightTracker");
        return;
      }

      setIsLoading(true);
      try {
        const currentUserId = localStorage.getItem("id") || undefined;
        const response = await axios.post(API_URL, {
          action: "get-freight-by-id",
          id,
          currentUserId,
        });

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );

        if (isApiSuccess(responseData) && responseData.data) {
          setFreight(responseData.data);
        } else {
          toast.error(
            responseData?.message || "Failed to fetch freight details",
            { autoClose: 2000 },
          );
          navigate("/frightTracker");
        }
      } catch (error) {
        console.error("Fetch freight error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(
          errData?.message ||
            "An error occurred while fetching freight details",
          { autoClose: 2000 },
        );
        navigate("/frightTracker");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFreight();
  }, [id, navigate]);

  const materials = Array.isArray(freight?.materials) ? freight.materials : [];
  const poNumbers = Array.isArray(freight?.poNumbers)
    ? freight.poNumbers.filter(Boolean).join(", ")
    : "-";
  const freightId = freight?._id || freight?.id || id;

  if (isLoading) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Freight</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/frightTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!freight) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Freight</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/frightTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Freight not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">View Freight</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/frightTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal">
                    <div className="card-body">
                      <FieldRow label="Freight Number">
                        {freight.freightNumber || "-"}
                      </FieldRow>
                      <FieldRow label="Customer Name">
                        {freight.customerName || "-"}
                      </FieldRow>
                      <FieldRow label="Freight Description">
                        {freight.freightDescription || "-"}
                      </FieldRow>
                      <FieldRow label="Freight Amount">
                        {formatAmount(freight.freightAmount)}
                      </FieldRow>
                      <FieldRow label="Freight Qty">
                        {freight.freightQty ?? "-"}
                      </FieldRow>
                      <FieldRow label="PO Number(s)">
                        {poNumbers || "-"}
                      </FieldRow>
                      <FieldRow label="Supplier Name(s)">
                        {(() => {
                          const list = Array.isArray(freight?.suppliers)
                            ? freight.suppliers
                            : [];
                          const names = list
                            .map((s) =>
                              String(
                                (typeof s === "string"
                                  ? s
                                  : s?.supplierName || s?.name || "") || "",
                              ).trim(),
                            )
                            .filter(Boolean);
                          return names.length > 0 ? names.join(", ") : "-";
                        })()}
                      </FieldRow>
                      <FieldRow label="Total PO Value">
                        {formatAmount(freight.totalPoValue)}
                      </FieldRow>
                      <FieldRow label="Freight Status">
                        {freightStatusBadge(freight.freightStatus)}
                      </FieldRow>
                      <FieldRow label="Remarks">
                        {freight.remarks || "-"}
                      </FieldRow>
                      

                      <div className="form-group row">
                        <label className="col-12 col-form-label fwt_600">
                          Materials
                        </label>
                        <div className="col-12">
                          {materials.length === 0 ? (
                            <div className="txtdsply text-muted">
                              No materials found.
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm mb-0">
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor: "#003366",
                                      color: "#fff",
                                    }}
                                  >
                                    <th style={{ fontSize: "12px" }}>S.No</th>
                                    <th style={{ fontSize: "12px" }}>
                                      PO Number
                                    </th>
                                    <th style={{ fontSize: "12px" }}>
                                      Material Code
                                    </th>
                                    <th style={{ fontSize: "12px" }}>
                                      Description
                                    </th>
                                    <th style={{ fontSize: "12px" }}>HSN</th>
                                    <th style={{ fontSize: "12px" }}>UOM</th>
                                    <th style={{ fontSize: "12px" }}>PO Qty</th>
                                    <th style={{ fontSize: "12px" }}>
                                      Freight Qty
                                    </th>
                                    <th style={{ fontSize: "12px" }}>
                                      Balance
                                    </th>
                                    <th style={{ fontSize: "12px" }}>
                                      Basic Price
                                    </th>
                                    <th style={{ fontSize: "12px" }}>
                                      Gross Basic
                                    </th>
                                    <th style={{ fontSize: "12px" }}>GST %</th>
                                    <th style={{ fontSize: "12px" }}>
                                      GST Value
                                    </th>
                                    <th style={{ fontSize: "12px" }}>
                                      Total Value
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {materials.map((row, index) => {
                                    const gross =
                                      row.grossbasicPrice ??
                                      row.grossBasicPrice ??
                                      row.grossValue;
                                    return (
                                      <tr key={row._id || row.id || index}>
                                        <td>{index + 1}</td>
                                        <td>{row.poNumber || "-"}</td>
                                        <td>{row.materialCode || "-"}</td>
                                        <td>
                                          {row.materialDescription || "-"}
                                        </td>
                                        <td>{row.hsnCode || row.hsn || "-"}</td>
                                        <td>{row.uom || "-"}</td>
                                        <td>{row.poQuantity ?? "-"}</td>
                                        <td>{row.freightQty ?? "-"}</td>
                                        <td>{row.balanceQuantity ?? "-"}</td>
                                        <td className="text-right">
                                          {formatAmount(row.basicPrice)}
                                        </td>
                                        <td className="text-right">
                                          {formatAmount(gross)}
                                        </td>
                                        <td className="text-right">
                                          {Number.isFinite(
                                            Number(row.gstPercent),
                                          )
                                            ? Number(row.gstPercent)
                                            : "-"}
                                        </td>
                                        <td className="text-right">
                                          {formatAmount(row.gstValue)}
                                        </td>
                                        <td className="text-right fwt_600">
                                          {formatAmount(row.totalValue)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                      <FieldRow label="Created By">
                        {displayUser(freight.createdBy)}
                        {freight.createdBy?.email && (
                          <span className="text-muted small d-block">
                            {freight.createdBy.email}
                          </span>
                        )}
                      </FieldRow>
                      <FieldRow label="Created On">
                        {formatDateToIST(freight.createdAt)}
                      </FieldRow>
                      <FieldRow label="Updated By">
                        {displayUser(freight.updatedBy)}
                        {freight.updatedBy?.email && (
                          <span className="text-muted small d-block">
                            {freight.updatedBy.email}
                          </span>
                        )}
                      </FieldRow>
                      <FieldRow label="Updated On">
                        {formatDateToIST(freight.updatedAt)}
                      </FieldRow>
                    </div>

                    <div className="card-footer text-center">
                      {String(freight.freightStatus || "")
                        .trim()
                        .toLowerCase() !== "paid" && (
                        <Link
                          to={`/frightTrackerEdit/${freightId}`}
                          className="btn btn-blue mr-2"
                        >
                          Edit
                        </Link>
                      )}
                      <Link to="/frightTracker" className="btn btn-secondary">
                        Back
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrightTrackerView;
